import { useState, useCallback, useEffect } from 'react';
import { readFromLocalStorage } from '@sbt-web/utils';

function useLocalStorage(key, initialValue) {
    if (!key) {
        throw new Error('useLocalStorage key may not be falsy');
    }
    const [state, setState] = useState(typeof window !== 'undefined'
        ? readFromLocalStorage(key, initialValue)
        : undefined);
    const set = useCallback((valOrFunc) => {
        try {
            let newState;
            if (valOrFunc instanceof Function) {
                newState = valOrFunc(state);
            }
            else {
                newState = valOrFunc;
            }
            if (typeof newState === 'undefined') {
                return;
            }
            const value = JSON.stringify(newState);
            // When the internal state is an object, each call to setState gets a new
            // reference and triggers the update of this useCallback.
            // Preventing the setState stops infinite loops of useEffect which
            // use `set` to write objects to the storage.
            if (value === JSON.stringify(state)) {
                return;
            }
            localStorage.setItem(key, value);
            setState(newState);
            // We manually fire a storage event in the same window
            // as otherwise this event is only dispatched when the
            // storage has been modified in a separate document/tab
            window.dispatchEvent(new StorageEvent('storage', { key, newValue: value }));
        }
        catch {
            // If user is in private mode or has storage restriction
            // localStorage can throw. JSON.parse and JSON.stringify
            // can throw, too.
            console.warn('Something errored in useLocalStorage. If this is a private browsing session the API will not work.');
        }
    }, [key, state]);
    const remove = useCallback(() => {
        try {
            localStorage.removeItem(key);
            setState(undefined);
            // Propagate change to other instances
            window.dispatchEvent(new StorageEvent('storage', { key, newValue: undefined }));
        }
        catch {
            // If user is in private mode or has storage restriction
            // localStorage can throw.
        }
    }, [key, setState]);
    // Listen for changes to the value made outside this hook's instance
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (!e)
                return;
            if (e.key === key) {
                try {
                    setState(() => (e.newValue ? JSON.parse(e.newValue) : undefined));
                }
                catch (e) {
                    console.warn('Could set new local storage value', e);
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [key]);
    return [state, set, remove];
}

export { useLocalStorage };
//# sourceMappingURL=useLocalStorage.js.map
